<template>
  <div>
    <!-- Vista de un solo contacto -->
    <div
      v-if="contacts.length === 1"
      class="flex items-center gap-3 p-3 rounded-lg transition-colors cursor-pointer"
      @click="showModal = true"
    >
      <div
        class="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center"
        :class="
          isSent
            ? 'bg-[var(--p-primary-200)]/50 dark:bg-[var(--p-primary-700)]/35'
            : 'bg-neutral-800/10 dark:bg-neutral-200/10'
        "
      >
        <User
          :size="20"
          :stroke-width="2"
          :class="
            isSent
              ? 'text-[var(--p-primary-900)] dark:text-[var(--p-primary-100)]'
              : 'text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]'
          "
        />
      </div>

      <div class="flex-1 min-w-0">
        <p
          class="font-medium text-sm leading-snug mb-1"
          :class="
            isSent
              ? 'text-[var(--p-primary-950)] dark:text-[var(--p-primary-50)]'
              : 'text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]'
          "
        >
          {{
            contacts[0].name?.formatted_name ||
            contacts[0].name?.first_name ||
            t('whatsapp.contact_message.unnamed')
          }}
        </p>

        <p
          v-if="contacts[0].phones && contacts[0].phones.length > 0"
          class="text-xs leading-relaxed"
          :class="
            isSent
              ? 'text-[var(--p-primary-900)]/75 dark:text-[var(--p-primary-100)]/75'
              : 'text-[var(--p-surface-700)] dark:text-[var(--p-surface-300)]'
          "
        >
          {{ contacts[0].phones[0].phone }}
        </p>
      </div>

      <ChevronRight
        :size="16"
        :stroke-width="2"
        class="flex-shrink-0 opacity-60"
        :class="
          isSent
            ? 'text-[var(--p-primary-900)] dark:text-[var(--p-primary-100)]'
            : 'text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]'
        "
      />
    </div>

    <!-- Vista de múltiples contactos -->
    <div
      v-else
      class="flex items-center gap-3 p-3 rounded-lg transition-colors cursor-pointer"
      @click="showModal = true"
    >
      <div
        class="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center"
        :class="
          isSent
            ? 'bg-[var(--p-primary-200)]/50 dark:bg-[var(--p-primary-700)]/35'
            : 'bg-neutral-800/10 dark:bg-neutral-200/10'
        "
      >
        <Users
          :size="20"
          :stroke-width="2"
          :class="
            isSent
              ? 'text-[var(--p-primary-900)] dark:text-[var(--p-primary-100)]'
              : 'text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]'
          "
        />
      </div>

      <div class="flex-1 min-w-0">
        <p
          class="font-medium text-sm leading-snug"
          :class="
            isSent
              ? 'text-[var(--p-primary-950)] dark:text-[var(--p-primary-50)]'
              : 'text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]'
          "
        >
          {{ t('whatsapp.contact_message.contacts_count', { count: contacts.length }) }}
        </p>
      </div>

      <ChevronRight
        :size="16"
        :stroke-width="2"
        class="flex-shrink-0 opacity-60"
        :class="
          isSent
            ? 'text-[var(--p-primary-900)] dark:text-[var(--p-primary-100)]'
            : 'text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]'
        "
      />
    </div>

    <!-- Modal de detalles -->
    <Dialog v-model:visible="showModal" modal :style="{ width: '450px' }" :dismissableMask="true">
      <template #header>
        <div class="flex items-center gap-2">
          <User :size="20" :stroke-width="2" />
          <span class="font-semibold">
            {{
              contacts.length === 1
                ? t('whatsapp.contact_message.contact')
                : t('whatsapp.contact_message.contacts_count', { count: contacts.length })
            }}
          </span>
        </div>
      </template>

      <div class="flex flex-col gap-3 max-h-[400px] overflow-y-auto pr-2">
        <div
          v-for="(contact, index) in contacts"
          :key="index"
          class="flex gap-3 p-3 rounded-lg bg-[var(--p-surface-50)] dark:bg-[var(--p-surface-800)]"
        >
          <div
            class="flex-shrink-0 w-11 h-11 rounded-full bg-[var(--p-primary-color)]/10 flex items-center justify-center"
          >
            <User :size="20" :stroke-width="2" class="text-[var(--p-primary-color)]" />
          </div>

          <div class="flex-1 min-w-0">
            <p class="font-medium text-sm leading-snug mb-2 text-[var(--p-text-color)]">
              {{
                contact.name?.formatted_name ||
                contact.name?.first_name ||
                t('whatsapp.contact_message.unnamed')
              }}
            </p>

            <div v-if="contact.phones && contact.phones.length > 0" class="flex flex-col gap-2">
              <a
                v-for="(phone, phoneIndex) in contact.phones"
                :key="phoneIndex"
                :href="`tel:${phone.phone}`"
                class="inline-flex items-center gap-2 text-xs leading-relaxed hover:underline w-fit text-[var(--p-text-muted-color)] hover:text-[var(--p-primary-color)] transition-colors"
              >
                <Phone :size="14" :stroke-width="2" />
                <span>{{ phone.phone }}</span>
                <span v-if="phone.type" class="opacity-60 text-xs">
                  ({{ formatPhoneType(phone.type) }})
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { User, Users, Phone, ChevronRight } from 'lucide-vue-next'
import Dialog from 'primevue/dialog'
import { useI18n } from 'vue-i18n'

interface ContactName {
  first_name?: string
  formatted_name?: string
  last_name?: string
}

interface ContactPhone {
  phone: string
  type?: string
}

interface Contact {
  name?: ContactName
  phones?: ContactPhone[]
}

interface Props {
  contacts: Contact[]
  isSent: boolean
}

defineProps<Props>()
const { t } = useI18n()

const showModal = ref(false)

const formatPhoneType = (type: string): string => {
  const types: Record<string, string> = {
    MOBILE: t('whatsapp.contact_message.phone_mobile'),
    HOME: t('whatsapp.contact_message.phone_home'),
    WORK: t('whatsapp.contact_message.phone_work'),
    OTHER: t('whatsapp.contact_message.phone_other'),
    otro: t('whatsapp.contact_message.phone_other'),
  }
  return types[type] || type.toLowerCase()
}
</script>
